import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "./AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { scrollToSection } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  Loader2,
  Target,
  Sparkles,
  LayoutDashboard,
  Home,
  Menu,
  X,
  User,
  LogOut,
  MessageCircle,
} from "lucide-react";
import { useState, useEffect } from "react";

export const Navigation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if we're on an authenticated app route (not marketing/landing)
  const isDashboardRoute = location.pathname.startsWith('/dashboard') ||
                          location.pathname.startsWith('/analytics') ||
                          location.pathname.startsWith('/create') ||
                          location.pathname.startsWith('/edit') ||
                          location.pathname.startsWith('/account') ||
                          location.pathname.startsWith('/settings');

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      navigate("/");
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle navigation link click
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false); // Close mobile menu
    
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: href.substring(2) } });
    } else {
      // If we're already on the home page, just scroll to the section
      scrollToSection(href.substring(2));
    }
  };

  // Effect to handle initial scroll when navigating from another page
  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      // Small delay to ensure content is loaded
      setTimeout(() => {
        scrollToSection(sectionId);
        // Clean up the state
        navigate(location.pathname, { replace: true, state: {} });
      }, 100);
    }
  }, [location.state]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const marketingLinks = [
    { name: "How It Works", link: "/#how-it-works" },
    { name: "Features", link: "/#features" },
    { name: "Pricing", link: "/#pricing" },
    { name: "FAQ", link: "/#faq" },
    { name: "Affiliates", link: "/affiliate", isRoute: true }
  ];

  // Custom logo component for WillTheyConvert
  const WillTheyConvertLogo = () => {
    return (
      <Link
        to="/"
        className="relative z-20 mr-4 flex items-center space-x-2 px-2 py-1 text-sm font-normal text-black"
      >
        {/* Empty logo - no text needed */}
      </Link>
    );
  };

  // If we're on dashboard routes, show the dashboard navigation style
  if (isDashboardRoute) {
    return (
      <nav className="border-b backdrop-blur-md supports-[backdrop-filter]:backdrop-blur-md sticky top-0 z-[100] transition-all duration-300 bg-background/95 shadow-sm border-transparent">
        <div className="container flex h-16 md:h-20 items-center justify-between px-4 sm:px-6 max-w-7xl relative">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center hover:opacity-80 transition-opacity duration-200"
            >
              <img
                src="/wtc-logo.png"
                alt="WillTheyConvert"
                width="137"
                height="56"
                className="h-10 sm:h-12 md:h-14 w-auto object-contain"
              />
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {user && (
              <>
                {/* Desktop navigation */}
                <Link to="/dashboard" className="hidden md:block">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 rounded-full border-2 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 h-10 px-5"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Dashboard</span>
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="hidden md:flex items-center gap-2 rounded-full border-2 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 h-10 px-4">
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          {user.email}
                          <ChevronDown className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[200] w-64 rounded-2xl p-2 shadow-xl border border-slate-100">
                    <div className="px-3 py-2 mb-1">
                      <p className="text-sm font-semibold text-slate-900">My Account</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer">
                      <Link to="/account" className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                          <User className="h-4 w-4 text-slate-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Account Settings</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer">
                      <Link to="/contact-support" className="flex items-center gap-3" onClick={(e) => {
                        e.preventDefault();
                        // Open contact support - will be handled via footer dialog
                        window.dispatchEvent(new CustomEvent('open-contact-support'));
                      }}>
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <MessageCircle className="h-4 w-4 text-indigo-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Contact Support</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      disabled={isLoading}
                      className="rounded-xl px-3 py-2.5 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                          {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                          ) : (
                            <LogOut className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        <span className="text-sm font-medium">Logout</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile menu for dashboard - improved styling */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="md:hidden flex items-center gap-2 rounded-full border-2 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 h-9 px-3"
                    >
                      <Menu className="h-4 w-4" />
                      <span className="text-xs font-medium max-w-[100px] truncate">{user.email?.split('@')[0]}</span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[200] w-64 rounded-2xl p-2 shadow-xl border border-slate-100">
                    <div className="px-3 py-2 mb-1">
                      <p className="text-sm font-semibold text-slate-900">My Account</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer">
                      <Link to="/dashboard" className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <LayoutDashboard className="h-4 w-4 text-indigo-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Dashboard</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer">
                      <Link to="/" className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Home className="h-4 w-4 text-slate-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Main Site</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer">
                      <Link to="/account" className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                          <User className="h-4 w-4 text-slate-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Account Settings</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 cursor-pointer">
                      <Link to="/contact-support" className="flex items-center gap-3" onClick={(e) => {
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent('open-contact-support'));
                      }}>
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <MessageCircle className="h-4 w-4 text-indigo-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">Contact Support</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      disabled={isLoading}
                      className="rounded-xl px-3 py-2.5 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                          {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                          ) : (
                            <LogOut className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        <span className="text-sm font-medium">Logout</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </nav>
    );
  }

  // For marketing pages, use modern enhanced style with dark glassmorphism
  return (
    <>
      <nav className="backdrop-blur-xl supports-[backdrop-filter]:backdrop-blur-xl sticky top-0 z-[100] transition-all duration-300 bg-gradient-to-r from-slate-900/95 via-slate-800/95 to-slate-900/95 border-b border-white/10 shadow-lg shadow-black/5">
        {/* Subtle gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>

        <div className="container flex h-20 md:h-24 items-center justify-between px-6 py-4 max-w-7xl relative">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center hover:opacity-80 transition-opacity duration-200 group"
            >
              <img
                src="/wtc-logo.png"
                alt="WillTheyConvert"
                width="156"
                height="64"
                className="h-12 sm:h-14 md:h-16 w-auto object-contain brightness-0 invert group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] transition-all duration-300"
              />
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            {marketingLinks.map((item, idx) => {
              if (item.isRoute) {
                return (
                  <Link
                    key={`nav-link-${idx}`}
                    to={item.link}
                    className="text-sm font-medium text-gray-200 hover:text-white transition-all duration-200 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 hover:after:w-full after:bg-gradient-to-r after:from-indigo-400 after:to-violet-400 after:transition-all after:duration-300 hover:drop-shadow-[0_0_8px_rgba(167,139,250,0.3)]"
                  >
                    {item.name}
                  </Link>
                );
              }
              return (
                <a
                  key={`nav-link-${idx}`}
                  href={item.link}
                  onClick={(e) => handleNavClick(e, item.link)}
                  className="text-sm font-medium text-gray-200 hover:text-white transition-all duration-200 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 hover:after:w-full after:bg-gradient-to-r after:from-indigo-400 after:to-violet-400 after:transition-all after:duration-300 hover:drop-shadow-[0_0_8px_rgba(167,139,250,0.3)]"
                >
                  {item.name}
                </a>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/dashboard" className="hidden md:block">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full px-5 h-10 flex items-center gap-2 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white border-none shadow-md hover:shadow-lg hover:shadow-violet-500/50 transition-all duration-200 font-semibold"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Dashboard</span>
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="hidden md:flex items-center gap-2 rounded-full border-2 border-gray-300 bg-white/95 hover:border-gray-400 hover:bg-white h-10 px-4 text-gray-900 hover:text-gray-900 backdrop-blur-sm transition-all duration-200 shadow-sm">
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <User className="h-4 w-4" />
                          <ChevronDown className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 z-[200]">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">Account</p>
                        <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="flex items-center gap-2 cursor-pointer">
                        <User className="h-4 w-4" />
                        Account Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      disabled={isLoading}
                      className="text-red-600 focus:text-red-600 cursor-pointer"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        "Logout"
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/auth" className="hidden md:block">
                  <Button variant="outline" size="sm" className="rounded-full px-5 h-10 font-medium bg-white/95 hover:bg-white text-gray-900 border-2 border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all duration-200">
                    Login
                  </Button>
                </Link>
                <Link to="/create" className="hidden md:block">
                  <Button
                    size="sm"
                    className="rounded-full px-5 h-10 flex items-center gap-2 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white border-none shadow-md hover:shadow-lg hover:shadow-violet-500/50 transition-all duration-200 font-semibold"
                  >
                    Test Your Idea <Sparkles className="h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile menu trigger */}
            <button
              className="md:hidden text-white hover:text-gray-200 transition-all duration-200 p-2 rounded-full border border-white/80 hover:border-white"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Outside navbar */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[200] md:hidden">
          {/* Full Screen Menu */}
          <div className="absolute inset-0 bg-white animate-in fade-in duration-300">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-indigo-500 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-violet-500 rounded-full blur-2xl animate-pulse delay-1000"></div>
              <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-indigo-400 rounded-full blur-xl animate-pulse delay-500"></div>
            </div>
            
            {/* Close Button */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 transition-all duration-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Menu Content */}
            <div className="flex flex-col items-center justify-center h-full px-8 text-center animate-in slide-in-from-bottom duration-500">
              {/* Logo */}
              <div className="mb-12 flex items-center justify-center">
                <img
                  src="/wtc-logo.png"
                  alt="WillTheyConvert"
                  width="195"
                  height="80"
                  className="h-20 w-auto object-contain max-w-[280px]"
                />
              </div>
              
              {/* Navigation Links */}
              <nav className="space-y-6 mb-12">
                {marketingLinks.map((item, idx) => {
                  if (item.isRoute) {
                    return (
                      <Link
                        key={`mobile-nav-${idx}`}
                        to={item.link}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block text-xl font-medium text-gray-700 hover:text-indigo-600 hover:scale-105 transition-all duration-300 transform"
                      >
                        {item.name}
                      </Link>
                    );
                  }
                  return (
                    <a
                      key={`mobile-nav-${idx}`}
                      href={item.link}
                      onClick={(e) => handleNavClick(e, item.link)}
                      className="block text-xl font-medium text-gray-700 hover:text-indigo-600 hover:scale-105 transition-all duration-300 transform"
                    >
                      {item.name}
                    </a>
                  );
                })}
              </nav>
              
              {/* Action Buttons */}
              <div className="space-y-4 w-full max-w-xs">
                {user ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full py-4 px-6 bg-gradient-to-r from-violet-500 to-indigo-500 text-white rounded-full font-semibold hover:from-violet-600 hover:to-indigo-600 hover:scale-105 transition-all duration-300 transform shadow-lg"
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/account"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full py-4 px-6 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-50 hover:scale-105 transition-all duration-300 transform"
                    >
                      Account Settings
                    </Link>
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleLogout();
                      }}
                      disabled={isLoading}
                      className="block w-full py-4 px-6 border-2 border-red-200 text-red-600 rounded-full font-semibold hover:bg-red-50 hover:border-red-300 hover:scale-105 transition-all duration-300 transform disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Logging out...
                        </span>
                      ) : (
                        "Logout"
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/create"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full py-4 px-6 bg-gradient-to-r from-violet-500 to-indigo-500 text-white rounded-full font-semibold hover:from-violet-600 hover:to-indigo-600 hover:scale-105 transition-all duration-300 transform shadow-lg"
                    >
                      Test Your Idea
                    </Link>
                    <Link
                      to="/auth"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full py-4 px-6 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-50 hover:scale-105 transition-all duration-300 transform"
                    >
                      Login
                    </Link>
                  </>
                )}
              </div>
              
              {/* Bottom Text */}
              <p className="mt-12 text-gray-500 text-sm">
                Validate your ideas before building
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
